import { Box, Button, Chip, Container, Stack, Typography } from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';
import LayoutShell from '../../../layout/LayoutShell';
import { useAppSelector } from '../../../store/hooks';
import { defaultAuthenticatedPath, isNoAccessUser } from '../userRoles';

function homeActionLabel(path: string): string {
  if (path === '/artifacts') {
    return 'Go to reflections';
  }

  if (path === '/admin') {
    return 'Go to admin';
  }

  return 'Go to home';
}

export default function NotFoundPage(): React.ReactElement {
  return (
    <LayoutShell>
      <NotFoundContent />
    </LayoutShell>
  );
}

export function NotFoundContent(): React.ReactElement {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const homePath = defaultAuthenticatedPath(user);

  if (isNoAccessUser(user)) {
    return <Navigate to="/no-access" replace />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Box
        sx={{
          background:
            'linear-gradient(180deg, rgba(244,247,245,0.98) 0%, rgba(232,238,234,0.98) 100%)',
          border: '1px solid #D9E3DD',
          borderRadius: 4,
          boxShadow: '0 24px 60px rgba(24, 44, 32, 0.12)',
          overflow: 'hidden',
          p: { xs: 4, md: 6 },
        }}
      >
        <Stack spacing={3}>
          <Chip
            label="404"
            sx={{
              alignSelf: 'flex-start',
              backgroundColor: '#D7E1DB',
              color: '#2B3B33',
              fontWeight: 700,
            }}
          />
          <Stack spacing={1.5}>
            <Typography sx={{ color: '#2B3B33', fontSize: { xs: 30, md: 42 }, fontWeight: 700 }}>
              This page doesn&apos;t exist
            </Typography>
            <Typography sx={{ color: '#42554B', fontSize: 17, lineHeight: 1.7, maxWidth: 720 }}>
              The link may be out of date, or the URL may have been typed incorrectly. You can go
              back to your main workspace or return to the previous page.
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              onClick={() => navigate(homePath)}
              sx={{
                backgroundColor: '#2B3B33',
                borderRadius: 999,
                color: '#F4F7F5',
                fontWeight: 700,
                px: 3,
                py: 1.25,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#223028',
                },
              }}
              variant="contained"
            >
              {homeActionLabel(homePath)}
            </Button>
            <Button
              onClick={() => navigate(-1)}
              sx={{
                borderColor: '#93A79A',
                borderRadius: 999,
                color: '#2B3B33',
                fontWeight: 700,
                px: 3,
                py: 1.25,
                textTransform: 'none',
              }}
              variant="outlined"
            >
              Go back
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Container>
  );
}
